//AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useEffect,
} from "react";
import { companyList } from "../../pages/CompanyProfilePage/modal";
import { UserModel } from "../../modal/UserAccess";

// Define the interface for context values
interface UserContextType {
  userId: number;
  setUserId: React.Dispatch<React.SetStateAction<number>>;
  companyProfileID: any;
  setCompanyProfileID: React.Dispatch<React.SetStateAction<number | null>>;
  isAppLoading: boolean;
  setIsApploading: React.Dispatch<React.SetStateAction<boolean>>;
  isAccessFetching: boolean;
  setNoCompaniesUnderClient: React.Dispatch<React.SetStateAction<boolean>>;
  noCompaniesUnderClient: boolean;
  setNoRoleAssigned: React.Dispatch<React.SetStateAction<boolean>>;
  noRoleAssigned: boolean;
  setIsAccessFecthing: React.Dispatch<React.SetStateAction<boolean>>;
  companyProlifeList: any[];
  setCompanyProfileList: React.Dispatch<React.SetStateAction<any[]>>;
  isSwitchAccountClick: boolean;
  setIsSwitchAccountClick: React.Dispatch<React.SetStateAction<boolean>>;
  isLogoutClick: boolean;
  setIsLogoutClick: React.Dispatch<React.SetStateAction<boolean>>;
  companyLogo: string | null;
  setCompanyLogo: React.Dispatch<React.SetStateAction<string | null>>;
  userAccess: UserModel;
  setUserAccess: React.Dispatch<React.SetStateAction<UserModel>>;
  showLogoutDialog: boolean;
  setShowLogoutDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create context with initial values
const UserContext = createContext<UserContextType | undefined>(undefined);

// Create a provider component to wrap your app
export const UserProvider = ({ children }: PropsWithChildren<{}>) => {
  const retrievedID = Number(localStorage.getItem("companyProfileID"));
  const retrievLogo = String(localStorage.getItem("companyLogo"));
  const retrievedUserAccess = localStorage.getItem("userAccess");

  const initialUserAccess = retrievedUserAccess
    ? JSON.parse(retrievedUserAccess)
    : [];
  const retrieveUserId = initialUserAccess?.userInformation?.userID || 0;
  const [userId, setUserId] = useState<number>(retrieveUserId);
  const [companyProfileID, setCompanyProfileID] = useState<any>(retrievedID);
  const [companyLogo, setCompanyLogo] = useState<string | null>(retrievLogo);
  const [userAccess, setUserAccess] = useState<UserModel>(initialUserAccess);
  const [isAppLoading, setIsApploading] = useState<boolean>(false);

  const [noCompaniesUnderClient, setNoCompaniesUnderClient] = useState<boolean>(false)
  const [noRoleAssigned, setNoRoleAssigned] = useState<boolean>(false)
  const [isAccessFetching, setIsAccessFecthing] = useState<boolean>(false);
  const [isSwitchAccountClick, setIsSwitchAccountClick] = useState(false);
  const [isLogoutClick, setIsLogoutClick] = useState(false);
  const [companyProlifeList, setCompanyProfileList] = useState<companyList[]>(
    []
  );
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      const newID = localStorage.getItem("companyProfileID");
      setCompanyProfileID(newID ? Number(newID) : null);

      const newLogo = localStorage.getItem("companyLogo");
      setCompanyLogo(newLogo ? String(newLogo) : null);

      const storedUserAccess = localStorage.getItem("userAccess");
      if (storedUserAccess) {
        setUserAccess(JSON.parse(storedUserAccess));
        const parsedAccess = JSON.parse(storedUserAccess);
        if (parsedAccess?.userInformation?.userID) {
          setUserId(parsedAccess.userInformation.userID); // Update userId from new userAccess
        }
      }
    };
    // Listen for storage changes
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  return (
    <UserContext.Provider
      value={{
        userId,
        setUserId,
        companyProfileID,
        setCompanyProfileID,
        isAppLoading,
        setIsApploading,
        companyProlifeList,
        setCompanyProfileList,
        isSwitchAccountClick,
        setIsSwitchAccountClick,
        isLogoutClick,
        setIsLogoutClick,
        companyLogo,
        setCompanyLogo,
        userAccess,
        setUserAccess,
        isAccessFetching,
        setIsAccessFecthing,
        showLogoutDialog,
        setShowLogoutDialog,
        noCompaniesUnderClient, setNoCompaniesUnderClient,
        noRoleAssigned, setNoRoleAssigned
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Create a custom hook for using AuthContext
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a AuthProvider");
  }
  return context;
};

export default UserContext;
